// Enhanced User Models for KYC Data and Approval Workflows

// Base User Model with Flexible Role System
export const BaseUser = {
  uid: '',
  userId: '', // 6-digit unique ID
  email: '',
  phone: '',
  // Flexible role system - users can have multiple capabilities
  capabilities: {
    canApplyForJobs: false,
    canBookServices: false,
    canProvideServices: false,
    canPostJobs: false,
    isAdmin: false
  },
  // Active roles based on enabled capabilities
  activeRoles: [], // ['job_seeker', 'employer', 'admin']
  // Primary role for routing and default dashboard
  primaryRole: '', // 'job_seeker', 'employer', 'admin'
  status: 'pending', // 'pending', 'approved', 'rejected', 'suspended'
  createdAt: null,
  updatedAt: null,
  lastLoginAt: null,
  profileCompleted: false,
  kycCompleted: false,
  documentsVerified: false,
  // Role-specific profile completion status
  roleProfiles: {
    job_seeker: { completed: false, kycCompleted: false },
    employer: { completed: false, kycCompleted: false }
  }
};

// Job Seeker Extended Profile
export const JobSeekerProfile = {
  // Personal Information
  personalInfo: {
    firstName: '',
    lastName: '',
    dateOfBirth: '',
    gender: '',
    nationalId: '', // NRC or Passport number
    address: {
      street: '',
      city: '',
      province: '',
      postalCode: '',
      country: 'Zambia'
    },
    profilePhoto: ''
  },
  
  // Education
  education: [
    {
      id: '',
      level: '', // 'High School', 'Bachelor', 'Master', etc.
      fieldOfStudy: '',
      institution: '',
      graduationYear: '',
      grade: '',
      current: false
    }
  ],
  
  // Work Experience
  workExperience: [
    {
      id: '',
      jobTitle: '',
      companyName: '',
      startDate: '',
      endDate: '',
      current: false,
      description: '',
      achievements: []
    }
  ],
  
  // Skills
  skills: [
    {
      name: '',
      level: '', // 'Beginner', 'Intermediate', 'Advanced', 'Expert'
      category: '' // 'Technical', 'Soft Skills', 'Language', etc.
    }
  ],
  
  // CV Information
  cv: {
    fileName: '',
    fileUrl: '',
    uploadedAt: null,
    fileSize: 0
  },
  
  // Employment Preferences
  preferences: {
    desiredJobRole: '',
    employmentType: [], // ['Full-time', 'Part-time', 'Contract', 'Remote']
    expectedSalaryRange: {
      min: 0,
      max: 0,
      currency: 'ZMW'
    },
    preferredLocations: [],
    availableStartDate: '',
    willingToRelocate: false
  },
  
  // Application History
  applications: [
    {
      jobId: '',
      appliedAt: null,
      status: '', // 'pending', 'viewed', 'shortlisted', 'interviewed', 'rejected', 'hired'
      notes: ''
    }
  ],
  
  // Saved Jobs
  savedJobs: []
};

// Employer Extended Profile (with merged service provider capabilities)
export const EmployerProfile = {
  // Company Details
  companyInfo: {
    companyName: '',
    industry: '',
    companySize: '', // '1-10', '11-50', '51-200', '201-500', '500+'
    registrationNumber: '',
    taxNumber: '',
    website: '',
    description: '',
    logo: '',
    address: {
      street: '',
      city: '',
      province: '',
      postalCode: '',
      country: 'Zambia'
    },
    contactInfo: {
      phone: '',
      email: '',
      alternatePhone: ''
    }
  },
  
  // Company Representative
  representative: {
    name: '',
    position: '',
    phone: '',
    email: '',
    linkedIn: ''
  },
  
  // Verification Documents
  documents: {
    businessLicense: {
      fileName: '',
      fileUrl: '',
      uploadedAt: null,
      verified: false
    },
    taxCertificate: {
      fileName: '',
      fileUrl: '',
      uploadedAt: null,
      verified: false
    },
    registrationCertificate: {
      fileName: '',
      fileUrl: '',
      uploadedAt: null,
      verified: false
    }
  },
  
  // Job Posting Preferences
  jobPostingPreferences: {
    categories: [], // Job categories they hire for
    recruitmentTeam: [
      {
        name: '',
        email: '',
        role: '',
        permissions: []
      }
    ]
  },
  
  // Posted Jobs
  postedJobs: [
    {
      jobId: '',
      title: '',
      status: '', // 'draft', 'pending_approval', 'active', 'closed', 'rejected'
      postedAt: null,
      applicationsCount: 0
    }
  ],
  
  // Service Management (merged from ServiceProviderProfile)
  services: [
    {
      id: '',
      serviceType: '',
      title: '',
      description: '',
      pricingStructure: '', // 'hourly', 'project', 'fixed'
      pricing: {
        hourlyRate: 0,
        projectRate: 0,
        fixedPrice: 0,
        currency: 'ZMW'
      },
      serviceLocation: '',
      availability: {
        Monday: { available: false, startTime: '', endTime: '' },
        Tuesday: { available: false, startTime: '', endTime: '' },
        Wednesday: { available: false, startTime: '', endTime: '' },
        Thursday: { available: false, startTime: '', endTime: '' },
        Friday: { available: false, startTime: '', endTime: '' },
        Saturday: { available: false, startTime: '', endTime: '' },
        Sunday: { available: false, startTime: '', endTime: '' }
      },
      status: '', // 'draft', 'pending_approval', 'active', 'paused', 'rejected'
      createdAt: null,
      updatedAt: null
    }
  ],
  
  // Service Portfolio
  portfolio: {
    images: [
      {
        url: '',
        caption: '',
        uploadedAt: null
      }
    ],
    videos: [
      {
        url: '',
        title: '',
        uploadedAt: null
      }
    ]
  },
  
  // Service Payment Information
  paymentInfo: {
    bankAccount: '',
    mobileMoneyNumber: '',
    preferredPaymentMethod: '', // 'bank', 'mobile_money'
    verified: false
  },
  
  // Service Bookings Management
  bookings: [
    {
      bookingId: '',
      clientId: '',
      serviceId: '',
      status: '', // 'pending', 'accepted', 'rejected', 'in_progress', 'completed', 'cancelled'
      scheduledDate: null,
      completedDate: null,
      amount: 0,
      rating: 0,
      review: ''
    }
  ],
  
  // Service Earnings
  serviceEarnings: {
    totalEarnings: 0,
    pendingPayments: 0,
    completedBookings: 0,
    averageRating: 0,
    totalReviews: 0
  },
  
  // Job Analytics
  analytics: {
    totalJobsPosted: 0,
    totalApplicationsReceived: 0,
    averageApplicationsPerJob: 0,
    successfulHires: 0
  }
};

// Admin Profile
export const AdminProfile = {
  permissions: [
    'user_management',
    'job_approval',
    'service_approval',
    'content_moderation',
    'analytics_access',
    'system_settings'
  ],
  lastActivity: null,
  activityLog: [
    {
      action: '',
      targetId: '',
      targetType: '', // 'user', 'job', 'service', 'system'
      timestamp: null,
      details: ''
    }
  ]
};

// Approval Workflow Models
export const ApprovalRequest = {
  id: '',
  type: '', // 'user_registration', 'job_posting', 'service_listing'
  requesterId: '',
  requesterRole: '',
  status: '', // 'pending', 'approved', 'rejected', 'requires_changes'
  submittedAt: null,
  reviewedAt: null,
  reviewedBy: '',
  reviewNotes: '',
  data: {}, // The actual data being reviewed
  priority: '', // 'low', 'medium', 'high'
  category: ''
};

// Notification Models
export const Notification = {
  id: '',
  userId: '',
  type: '', // 'booking_request', 'application_update', 'approval_status', 'system_announcement'
  title: '',
  message: '',
  data: {}, // Additional data for the notification
  read: false,
  createdAt: null,
  expiresAt: null,
  priority: '', // 'low', 'medium', 'high'
  actionRequired: false,
  actionUrl: ''
};

// Job Model (Enhanced)
export const Job = {
  id: '',
  jobId: '', // 6-digit unique ID
  employerId: '',
  title: '',
  description: '',
  requirements: [],
  responsibilities: [],
  category: '',
  employmentType: '', // 'full-time', 'part-time', 'contract', 'internship'
  location: {
    city: '',
    province: '',
    remote: false,
    hybrid: false
  },
  salary: {
    min: 0,
    max: 0,
    currency: 'ZMW',
    negotiable: false
  },
  benefits: [],
  status: '', // 'draft', 'pending_approval', 'active', 'closed', 'rejected'
  approvalStatus: {
    status: 'pending',
    reviewedBy: '',
    reviewedAt: null,
    notes: ''
  },
  postedAt: null,
  expiresAt: null,
  applicationsCount: 0,
  viewsCount: 0,
  featured: false
};

// Service Model (Enhanced)
export const Service = {
  id: '',
  serviceId: '', // 6-digit unique ID
  providerId: '',
  title: '',
  description: '',
  category: '',
  subcategory: '',
  pricing: {
    type: '', // 'hourly', 'fixed', 'package'
    amount: 0,
    currency: 'ZMW',
    packages: [
      {
        name: '',
        description: '',
        price: 0,
        deliveryTime: ''
      }
    ]
  },
  location: {
    city: '',
    province: '',
    serviceArea: [],
    onsite: true,
    remote: false
  },
  availability: {},
  media: {
    images: [],
    videos: []
  },
  status: '', // 'draft', 'pending_approval', 'active', 'paused', 'rejected'
  approvalStatus: {
    status: 'pending',
    reviewedBy: '',
    reviewedAt: null,
    notes: ''
  },
  createdAt: null,
  updatedAt: null,
  bookingsCount: 0,
  rating: 0,
  reviewsCount: 0,
  featured: false
};

// Application Model
export const Application = {
  id: '',
  jobId: '',
  jobSeekerId: '',
  employerId: '',
  status: '', // 'pending', 'viewed', 'shortlisted', 'interviewed', 'rejected', 'hired'
  appliedAt: null,
  statusUpdatedAt: null,
  coverLetter: '',
  cvUrl: '',
  additionalDocuments: [],
  notes: '',
  interviewScheduled: {
    date: null,
    time: '',
    location: '',
    type: '', // 'in-person', 'video', 'phone'
    notes: ''
  },
  feedback: {
    rating: 0,
    comments: '',
    strengths: [],
    improvements: []
  }
};

// Booking Model
export const Booking = {
  id: '',
  serviceId: '',
  providerId: '',
  clientId: '',
  status: '', // 'pending', 'accepted', 'rejected', 'in_progress', 'completed', 'cancelled'
  requestedDate: null,
  scheduledDate: null,
  completedDate: null,
  location: {
    address: '',
    coordinates: { lat: 0, lng: 0 }
  },
  serviceDetails: {
    description: '',
    requirements: [],
    estimatedDuration: '',
    specialInstructions: ''
  },
  pricing: {
    quotedAmount: 0,
    finalAmount: 0,
    currency: 'ZMW',
    paymentStatus: '' // 'pending', 'paid', 'refunded'
  },
  communication: [
    {
      senderId: '',
      message: '',
      timestamp: null,
      type: '' // 'message', 'status_update', 'system'
    }
  ],
  rating: {
    clientRating: 0,
    providerRating: 0,
    clientReview: '',
    providerReview: ''
  },
  createdAt: null,
  updatedAt: null
};

// System Settings Model
export const SystemSettings = {
  approvalWorkflows: {
    userRegistration: {
      job_seeker: { requiresApproval: false, autoApprove: true },
      employer: { requiresApproval: true, autoApprove: false }
    },
    contentApproval: {
      jobPostings: { requiresApproval: true, autoApprove: false },
      serviceListings: { requiresApproval: true, autoApprove: false }
    }
  },
  notifications: {
    emailEnabled: true,
    smsEnabled: true,
    pushEnabled: true,
    templates: {}
  },
  paymentSettings: {
    commissionRate: 0.05, // 5%
    minimumPayout: 50,
    payoutSchedule: 'weekly' // 'daily', 'weekly', 'monthly'
  }
};

export const UserModels = {
  BaseUser,
  JobSeekerProfile,
  EmployerProfile,
  AdminProfile,
  ApprovalRequest,
  Notification,
  Job,
  Service,
  Application,
  Booking,
  SystemSettings
};

export default UserModels;